import { useMutation } from "@apollo/client";
import { Grid, makeStyles } from "@material-ui/core";
import { requestStatusColor } from "components/Card/RequestCard";
import Dialog from "components/Dialog";
import { DefaultInfo } from "components/NothingRight";
import SnackBar from "components/SnackBar";
import TimeLineEventItem from "components/TimeLineEventItem";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { InationalTransportPoint, ITourPoint, NewRequestQuoteContext } from "contexts/RequestQuoteContext";
import { acceptRequest, declineRequest } from "domains/Request/graphql/mutation";
import { ERequestStatus, ERequestType } from "generated/graphql";
import { MotorCycleIcon, NTIcon, TourNewIcon, TreeCycleIcon, TruckIcon, VanIcon } from "icons";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Button } from "ui/Button";
import {
  ContainerRequestDetails,
  DefaultAnimatedDiv,
  RightSideSamaPapserContainer,
  StatusRequest,
  TimeLineContainer,
} from "./MainStyles";
import DetailsInfos from "./RightSideComponents/components/DetailsInfos";
import SchedulingDetails from "./RightSideComponents/components/SchedulingDetails";
import VehicleDetails from "./RightSideComponents/components/VehicleDetails";
import DevisRequestQuote from "./RightSideComponents/DevisRequestQuote";
import TimeLineRequestPoint from "./RightSideComponents/TimeLineRequestPoint";
import { useStyles } from "./TourSideRecap";
import { getRelativeRequestInformation } from "./utils";

export const requestContainerBgColor = {
  MAD: "252, 254, 255",
  NT: "194,202,236",
  TR: "202, 234, 195",
};

export const IconType = {
  MAD: <VanIcon />,
  NT: <NTIcon />,
  TR: <TourNewIcon />,
};

export const VehicleIconType = {
  SCOOTER: <MotorCycleIcon width={45} height={45} />,
  MINI_VAN: <VanIcon width={45} height={45} />,
  VAN: <TruckIcon width={45} height={45} />,
  TRUCK: <TreeCycleIcon width={45} height={45} />,
};

const TimeLineComponentType = {
  NT: (stops: InationalTransportPoint[]) => <TimeLineRequestPoint pointsNT={stops} type={ERequestType.Nt} />,
  TR: (stops: ITourPoint[]) => <TimeLineRequestPoint pointsTour={stops} type={ERequestType.Tr} />,
};
interface IRightSideDetails {
  isForDetails?: boolean;
}

export const useDetailsStyles = makeStyles((theme) => ({
  marge: {
    width: "48%",
    margin: "20px 0 0px",
    "&:nth-child(1)": {
      marginRight: "15px",
    },
    "&:nth-child(3)": {
      marginRight: "15px",
    },
    [theme.breakpoints.down("xs")]: {
      width: "48%",
      "&:nth-child(1)": {
        marginRight: "12px",
      },
      "&:nth-child(3)": {
        marginRight: "12px",
      },
    },
  },

  parcelCardGrid: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    margin: "10px 0",
  },

  additionalInfos: {
    display: "flex",
    flexDirection: "column",
    marginTop: "20px",
  },
  additionalInfo: {
    height: "60px",
    color: "#757a93",
    fontSize: "14px",
    fontWeight: "normal",
    fontStretch: "normal",
    lineHeight: 1.79,
    letterSpacing: "normal",
    textAlign: "justify",
    padding: "0 28px 0 0",
  },

  details: {
    marginBottom: "-20px",
    color: "#399ebf",
    fontSize: "14px",
    fontWeight: "bold",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "normal",
    letterSpacing: "normal",
    [theme.breakpoints.down("xs")]: {},
  },

  hr: {
    position: "relative",
    top: "-15px",
    width: "80%",
    right: "-44px",
    height: "2px",
    border: "none",
    backgroundColor: "rgba(57, 158, 191, 0.2)",
    [theme.breakpoints.down("xs")]: {
      width: "67%",
      right: "-47px",
    },
  },
  hr4: {
    position: "relative",
    top: "-15px",
    width: "70%",
    right: "-61px",
    height: "2px",
    border: "none",
    backgroundColor: "rgba(57, 158, 191, 0.2)",
  },
}));

export default function RightSideDetails({ isForDetails = false }: IRightSideDetails) {
  const { step, requestQuote, requestQuoteTour, setRequestQuoteDetails, requestQuoteNT, newRequestBadgeTimeOut } =
    React.useContext(NewRequestQuoteContext);
  const [requestToDisplay, setRequestToDisplay] = useState<any>(null);
  const { text } = useText();
  const [acceptRequestForClient] = useMutation(acceptRequest);
  const [declineRequestForClient] = useMutation(declineRequest);
  const dispatchAlert = useAlertDispatch();
  const [confirm, setConfirm] = React.useState(false);
  const [isAccept, setIsAccept] = React.useState(true);
  const styles = useStyles();

  useEffect(() => {
    setRequestToDisplay(requestQuote || requestQuoteNT || requestQuoteTour);
    // eslint-disable-next-line
  }, [requestQuote, requestQuoteNT, requestQuoteTour]);

  const renderEvents = () =>
    requestToDisplay?.request_activities?.map((activity: any, index: any) => (
      <TimeLineEventItem
        key={index}
        date={moment(activity?.activity_date).format("L")}
        time={moment(activity?.activity_date).format("LT")}
        dotColor={requestStatusColor[activity?.activity_trigger_action_data]}
        event={
          <StatusRequest
            background-color={requestStatusColor[activity?.activity_trigger_action_data]}
            style={{ width: "100%" }}
          >
            {activity?.activity_trigger_action_data?.replace(/_/g, " ")}
          </StatusRequest>
        }
      />
    ));

  const acceptRequestHandler = () => {
    try {
      acceptRequestForClient({
        variables: {
          input: { id: requestToDisplay?._id },
        },
      })
        .then((response) => {
          setConfirm(false);
          const {
            data: { acceptRequest },
          } = response;
          if (acceptRequest?.code === 200) {
            setRequestQuoteDetails(acceptRequest?.data);
            dispatchAlert({
              message: text.requestQuote.accept_request,
              severity: "success",
            });
          } else {
            return dispatchAlert({
              message: acceptRequest?.message || "",
              severity: "error",
            });
          }
        })
        .catch((err: any) => {
          setConfirm(false);
        });
    } catch (error) {
      setConfirm(false);
    }
  };

  const declineRequestHandler = () => {
    try {
      declineRequestForClient({
        variables: {
          input: { id: requestToDisplay?._id },
        },
      })
        .then((response) => {
          setConfirm(false);
          const {
            data: { declineRequest },
          } = response;
          if (declineRequest?.code === 200) {
            setRequestQuoteDetails(declineRequest?.data);
            dispatchAlert({
              message: text.requestQuote.decline_request,
              severity: "success",
            });
          } else {
            return dispatchAlert({
              message: declineRequest?.message || "",
              severity: "error",
            });
          }
        })
        .catch((err) => {
          setConfirm(false);
        });
    } catch (error) {
      setConfirm(false);
    }
  };
  const renderTimeLinePoint = () =>
    requestToDisplay?.type &&
    requestToDisplay?.type !== ERequestType.Mad &&
    TimeLineComponentType[requestToDisplay?.type](requestToDisplay?.stops || requestToDisplay?.templateStops);

  return (
    <RightSideSamaPapserContainer>
      {requestToDisplay?.vehicles?.length && step !== 3 ? (
        <>
          <Grid>
            <VehicleDetails requestToDisplay={requestQuote} />
            <br />
          </Grid>
          {requestToDisplay?.productType && (
            <Grid>
              <SchedulingDetails requestToDisplay={requestQuote} />
            </Grid>
          )}

          {requestToDisplay?.dispatcher && (
            <Grid>
              <DetailsInfos requestToDisplay={requestQuote} />
            </Grid>
          )}

          {renderTimeLinePoint()}
          {isForDetails && requestToDisplay?.price > 0 && (
            <ContainerRequestDetails>
              <DevisRequestQuote type={requestToDisplay?.type} request={requestToDisplay} />
              <DefaultAnimatedDiv>
                {requestToDisplay?.view?.byClient && !newRequestBadgeTimeOut && (
                  <SnackBar
                    severity="info"
                    content={getRelativeRequestInformation(requestToDisplay?.status, text)}
                    color={`rgb(${requestStatusColor[requestToDisplay?.status]})`}
                    bgColor={`rgba(${requestStatusColor[requestToDisplay?.status]}, 0.1)`}
                  />
                )}
                <TimeLineContainer>{renderEvents()}</TimeLineContainer>
                {requestToDisplay?.status === ERequestStatus.Requested && (
                  <>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      style={{ margin: "10px 0px 7px 0px" }}
                      onClick={() => {
                        setConfirm(true);
                        setIsAccept(true);
                      }}
                    >
                      {text.all.accept}
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setConfirm(true);
                        setIsAccept(false);
                      }}
                    >
                      {text.all.decline}
                    </Button>
                  </>
                )}
              </DefaultAnimatedDiv>
            </ContainerRequestDetails>
          )}
        </>
      ) : (
        <DefaultInfo />
      )}
      {confirm && (
        <Dialog
          title={text.all.confirmation}
          open={confirm}
          confirmButtonTitle={text.all.confirm}
          handleClose={() => {
            setConfirm(false);
          }}
          handleConfirm={isAccept ? acceptRequestHandler : declineRequestHandler}
        >
          {isAccept ? text.all.are_you_want_accept_request : text.all.are_you_want_decline_request}
        </Dialog>
      )}
    </RightSideSamaPapserContainer>
  );
}
